import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ListTodo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { celebrationService } from '@/lib/api/celebration.service';

interface PromotionBlockingTask {
  id: number;
  title: string;
  status: string;
  due_date?: string | null;
  event_id?: number | null;
  event_title?: string | null;
}

interface CelebrationPromotionReadiness {
  currentStatus: string;
  targetStatus: string | null;
  canPromote: boolean;
  eventPlanningBlockers: Array<{
    event_id: number | null;
    event_title: string;
    status: string;
    has_activities: boolean;
    reasons: string[];
  }>;
  blockingTasks: PromotionBlockingTask[];
}

export const CelebrationPromotionReadinessPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<CelebrationPromotionReadiness | null>(null);

  const celebrationId = id ? Number(id) : 0;

  const openTask = useCallback((taskId: number) => {
    navigate(`/celebrations/${celebrationId}/tasks?taskId=${taskId}`, {
      state: {
        highlightTaskId: String(taskId),
        openTaskId: String(taskId),
      },
    });
  }, [celebrationId, navigate]);

  const loadReadiness = useCallback(async () => {
    if (!celebrationId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await celebrationService.getPromotionReadiness(celebrationId);
      setReadiness(data);
    } catch (err) {
      const apiError = err as { response?: { data?: { message?: string } } };
      setError(apiError?.response?.data?.message || 'Failed to load promotion readiness details.');
    } finally {
      setLoading(false);
    }
  }, [celebrationId]);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  if (loading) {
    return <PageState kind="loading" title="Loading promotion readiness..." width="wide" />;
  }

  if (error || !readiness) {
    return (
      <PageState
        kind="error"
        title="Promotion readiness unavailable"
        description={error || 'Could not load promotion readiness.'}
        width="wide"
        action={
          <Button variant="outline" size="sm" onClick={() => navigate(`/celebrations/${celebrationId}`)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to celebration
          </Button>
        }
      />
    );
  }

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs
        items={[
          { label: 'Home', to: '/home' },
          { label: 'Celebration', to: `/celebrations/${celebrationId}` },
          { label: 'Promotion readiness' },
        ]}
      />

      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <h1 className="text-xl font-semibold text-foreground">Promotion readiness</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Current status: <span className="font-medium">{readiness.currentStatus}</span>
          {readiness.targetStatus && (
            <> | Next target: <span className="font-medium">{readiness.targetStatus}</span></>
          )}
        </p>

        {readiness.canPromote ? (
          <div className="mt-4 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
            This celebration is ready to be promoted.
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Promotion is blocked until all event blockers and tasks are resolved.
          </div>
        )}
      </div>

      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <ListTodo className="h-4 w-4 text-amber-600" />
          <h2 className="text-base font-semibold text-foreground">Event planning blockers</h2>
          <Badge variant="secondary" size="sm">{readiness.eventPlanningBlockers.length}</Badge>
        </div>

        {readiness.eventPlanningBlockers.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No event-level blockers found.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {readiness.eventPlanningBlockers.map((blocker, index) => (
              <li key={`${blocker.event_id ?? 'none'}-${index}`} className="rounded-md border px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{blocker.event_title}</span>
                  <span className="text-[10px] uppercase tracking-wide text-amber-700">{blocker.status}</span>
                </div>
                <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">
                  {blocker.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex gap-2">
          <Button size="sm" onClick={() => navigate(`/celebrations/${celebrationId}/events`)}>
            Open celebration events
          </Button>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <ListTodo className="h-4 w-4 text-amber-600" />
          <h2 className="text-base font-semibold text-foreground">Blocking tasks</h2>
          <Badge variant="secondary" size="sm">{readiness.blockingTasks.length}</Badge>
        </div>

        {readiness.blockingTasks.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No blocking tasks found.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {readiness.blockingTasks.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  onClick={() => openTask(task.id)}
                  className="w-full rounded-md border px-3 py-2 text-left transition-colors hover:bg-amber-50"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{task.title}</span>
                    <span className="text-[10px] uppercase tracking-wide text-amber-700">{task.status}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {task.event_title ? `Event: ${task.event_title}` : 'Event: Celebration-level task'}
                    {task.due_date ? ` | Due: ${new Date(task.due_date).toLocaleDateString()}` : ''}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex gap-2">
          <Button size="sm" onClick={() => navigate(`/celebrations/${celebrationId}/tasks`)}>
            Open celebration tasks
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(`/celebrations/${celebrationId}`)}>
            Back to celebration
          </Button>
        </div>
      </div>
    </PageContainer>
  );
};
